import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Phone } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const LOGO_URL = "/logo.png";

const navLinks = [
  {
    name: 'Leistungen',
    href: '/leistungen',
    children: [
      { name: 'Wertermittlung', href: '/leistungen/wertermittlung' },
      { name: 'Verkauf', href: '/leistungen/verkauf' },
      { name: 'Vermietung', href: '/leistungen/vermietung' },
      { name: 'Vermittlung', href: '/leistungen/vermittlung' },
    ]
  },
  { name: 'Immobilien', href: '/immobilien' },
  { name: 'Referenzen', href: '/referenzen' },
  { name: 'Über uns', href: '/ueber-uns' },
  { name: 'FAQ', href: '/faq' },
  { name: 'Kontakt', href: '/kontakt' },
];

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setDropdownOpen(false);
  }, [location]);

  return (
    <nav
      data-testid="main-navbar"
      className={`fixed w-full z-50 transition-all duration-300 ${
        isScrolled || mobileMenuOpen
          ? 'bg-[#131d30]/95 backdrop-blur-xl border-b border-[#d1af6a]/20'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="navbar-logo">
            <img 
              src={LOGO_URL} 
              alt="Dogan Immobilien" 
              className="h-12 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <div key={link.name} className="relative">
                {link.children ? (
                  <div
                    className="relative"
                    onMouseEnter={() => setDropdownOpen(true)}
                    onMouseLeave={() => setDropdownOpen(false)}
                  >
                    <button
                      className="nav-link flex items-center gap-1"
                      data-testid={`nav-${link.name.toLowerCase()}`}
                    >
                      {link.name}
                      <ChevronDown className="w-4 h-4" />
                    </button>
                    <AnimatePresence>
                      {dropdownOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 10 }}
                          transition={{ duration: 0.2 }}
                          className="absolute top-full left-0 pt-2"
                        >
                          <div className="bg-[#1a2640] border border-[#d1af6a]/20 rounded-none min-w-[200px] shadow-xl">
                            {link.children.map((child) => (
                              <Link
                                key={child.href}
                                to={child.href}
                                className="block px-4 py-3 text-white hover:text-[#d1af6a] hover:bg-[#d1af6a]/5 transition-colors text-sm font-medium"
                                data-testid={`nav-${child.name.toLowerCase()}`}
                              >
                                {child.name}
                              </Link>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <Link
                    to={link.href}
                    className="nav-link"
                    data-testid={`nav-${link.name.toLowerCase().replace(' ', '-')}`}
                  >
                    {link.name}
                  </Link>
                )}
              </div>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center gap-4">
            <a
              href="tel:+491756900374"
              className="flex items-center gap-2 text-[#c8c6c4] hover:text-[#d1af6a] transition-colors text-sm"
              data-testid="navbar-phone"
            >
              <Phone className="w-4 h-4" />
              <span className="hidden xl:inline">+49 175 6900374</span>
            </a>
            <Link
              to="/leistungen/wertermittlung"
              className="btn-primary text-sm"
              data-testid="navbar-cta"
            >
              Kostenlose Bewertung
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden text-white p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
            aria-label="Menü öffnen"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-[#131d30] border-t border-[#d1af6a]/20"
          >
            <div className="px-4 py-6 space-y-4">
              {navLinks.map((link) => (
                <div key={link.name}>
                  {link.children ? (
                    <div className="space-y-2">
                      <span className="block text-[#d1af6a] font-accent text-xs tracking-widest">
                        {link.name}
                      </span>
                      {link.children.map((child) => (
                        <Link
                          key={child.href}
                          to={child.href}
                          className="block py-2 text-white hover:text-[#d1af6a] transition-colors pl-4"
                        >
                          {child.name}
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <Link
                      to={link.href}
                      className="block py-2 text-white hover:text-[#d1af6a] transition-colors font-medium"
                    >
                      {link.name}
                    </Link>
                  )}
                </div>
              ))}
              <div className="pt-4 border-t border-[#d1af6a]/20">
                <Link
                  to="/leistungen/wertermittlung"
                  className="btn-primary w-full text-center block"
                >
                  Kostenlose Bewertung
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
